import './openAiCompletion.html';
import './openAiCompletion.scss';

import {inject, injectable} from "inversify";
import {wccModules} from "enums/wccModules";
import autobind from "decorators/autobind";
import {Observable, Subscribable} from "knockout";
import {EnumValue} from "interfaces/enumValue";
import {IOpenAiResponse, IReplySuggestionJson} from "models/openAI";

interface IOpenAiCompletionConfig {
    threadId: string
    replySuggestions: IReplySuggestionJson[]
    openAiCompletionCallback?: ((completion: string) => void) | undefined
    suggestionsLoadedCallback?: ((suggestion: IReplySuggestionJson[]) => void) | undefined
    ref: Observable<OpenAiCompletion>
    cHasModReply: Subscribable<boolean>
    collapseIfCommented: boolean
    preLoadExisting?: boolean
    tasksSite?: boolean
    translationNeeded: Observable<boolean>
}

class OpenAiResponse {
    constructor(public Response: IOpenAiResponse, public Type: EnumValue) {
        
    }
}

interface IOpenAiCompletionViewType {
    text: string
    value: EnumValue
    icon: string
    isSeparator?: boolean
}

const GenerateCommentViewType = { text: 'Generate', value: -1 };

class OpenAiCompletionView {
    type: EnumValue;
    title: string;
    icon: string;
    isSeparator: boolean;

    oIsSelected = ko.observable(false);
    
    constructor(data: IOpenAiCompletionViewType, isSelected: boolean) {
        this.type = data.value;
        this.title = data.text;
        this.icon = data.icon;
        this.isSeparator = data.isSeparator == true;
        
        this.oIsSelected(isSelected);
    }
}

const _clipboard = require('Scripts/clipboard.2.0.0.min');

class OpenAiCompletionSentence {
    suggestionText: string;
    suggestionTranslatedText: string;
    
    cDisplayString = ko.pureComputed(() => {
        let suggestionText = system.stripHtml(this.suggestionText);
        if (this.suggestionTranslatedText) {
            suggestionText = `<div>${suggestionText}</div> <div class="open-ai-completion__completion__translation">${system.stripHtml(this.suggestionTranslatedText)}</div>`;
        }
        return suggestionText;
    });
    
    constructor(data: IReplySuggestionJson, public oTranslationNeeded: Observable<boolean>) {
        this.suggestionText = data.SuggestionText?.trim();
        this.suggestionTranslatedText = data.SuggestionTranslatedText?.trim();
    }
}

@injectable()
export default class OpenAiCompletion {
        
    busy = ko.observable(false);
    oIsInSelectionState = ko.observable(false);

    notNativeSpeaker = WCC.Settings.NotNativeSpeaker === true;
    collapsedHeaderText: string;

    completionViews: OpenAiCompletionView[];
    oSelectedView: Observable<OpenAiCompletionView>;

    oThankYouCompletions = ko.observableArray<OpenAiCompletionSentence>();
    oTellMeMoreCompletions = ko.observableArray<OpenAiCompletionSentence>();
    oGenerateCommentCompletions = ko.observableArray<OpenAiCompletionSentence>();

    cGenerationViewSelected = ko.pureComputed(() => this.oSelectedView().type.value == GenerateCommentViewType.value);
    
    cCompletionsToDisplay = ko.pureComputed(() => {
        switch(this.oSelectedView().type.value) {
            case GenerateCommentViewType.value:
                return this.oGenerateCommentCompletions();
                
            case enums.OpenAiReplySuggestionType.ThankYou.value:
                return this.oThankYouCompletions();
                
            default:
                return this.oTellMeMoreCompletions();
        }
    });
    cHasCompletions = ko.pureComputed(() => this.cCompletionsToDisplay().length > 0);
    
    oTranslationNeeded: Observable<boolean>

    oCommentGenerationPrompt = ko.observable('');
    commentGenerationPromptPlaceholder = labels.RequestACommentPrompt;
    generateBtnText = labels.Generate;
    oCommentGenerationPromptFocused = ko.observable(false);
    
    private onCompletionSelected: ((completion: string, translatedCompletion: string | undefined) => void) | undefined;
    private onSuggestionsLoaded: ((suggestions: IReplySuggestionJson[]) => void) | undefined;
    private threadId: string;
    private tasksSite: boolean;

    constructor(
        @inject(wccModules.componentConfig) private config: IOpenAiCompletionConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
    ) {
        this.onCompletionSelected = config.openAiCompletionCallback;
        this.onSuggestionsLoaded = config.suggestionsLoadedCallback;
        this.threadId = ko.unwrap(config.threadId);
        this.tasksSite = config.tasksSite ?? false;
        this.oTranslationNeeded = config.translationNeeded;

        this.collapsedHeaderText = this.notNativeSpeaker && WCC.Settings.ReplySuggestionLabels.SuggestedResponses
            ? WCC.Settings.ReplySuggestionLabels.SuggestedResponses : labels.SuggestedResponses;
        const OpenAiCompletionViews: IOpenAiCompletionViewType[] = [
            { 
                text: this.notNativeSpeaker && WCC.Settings.ReplySuggestionLabels.ReplySuggestionThankYou 
                    ? WCC.Settings.ReplySuggestionLabels.ReplySuggestionThankYou : labels.ReplySuggestionThankYou, 
                value: enums.OpenAiReplySuggestionType.ThankYou, 
                icon: 'fal fa-hands-clapping' 
            },
            { 
                text: this.notNativeSpeaker && WCC.Settings.ReplySuggestionLabels.ReplySuggestionTellMeMore
                    ? WCC.Settings.ReplySuggestionLabels.ReplySuggestionTellMeMore : labels.ReplySuggestionTellMeMore, 
                value: enums.OpenAiReplySuggestionType.TellMeMore, 
                icon: 'fal fa-comment-dots' },
            { text: '', value: { text: '', value: -2 }, icon: '', isSeparator: true },
            { 
                text: this.notNativeSpeaker && WCC.Settings.ReplySuggestionLabels.ReplySuggestionGenerateAComment
                    ? WCC.Settings.ReplySuggestionLabels.ReplySuggestionGenerateAComment : labels.ReplySuggestionGenerateAComment, 
                value: GenerateCommentViewType, 
                icon: 'fal fa-message-plus' },
        ];
        this.completionViews = OpenAiCompletionViews.map(t => 
            new OpenAiCompletionView(t, t.value == enums.OpenAiReplySuggestionType.ThankYou));
        this.oSelectedView = ko.observable(this.completionViews[0]);
        
        this.fillSuggestions(ko.unwrap(config.replySuggestions));
        
        // config.cHasModReply() - this is not really working on tasks site
        if(ko.unwrap(config.replySuggestions).length && (!config.collapseIfCommented || !config.cHasModReply())) {
            this.oIsInSelectionState(true);
        }
        
        if(config.preLoadExisting) {
            this.loadExistingCompletions(!config.collapseIfCommented || !config.cHasModReply());
        }

        config.ref(this);
    }
    
    @autobind
    switchToSelectionState() {
        this.oIsInSelectionState(true);
        if(!this.cGenerationViewSelected())
            this.getNewCompletions();
    }
    
    findViewByType(typeVal: number) {
        return _.find(this.completionViews, v => v.type.value == typeVal) ?? this.completionViews[0];
    };
    
    @autobind
    exitSelectionState() {
        this.oIsInSelectionState(false);
        this.selectCompletionView(this.completionViews[0], true);
    }
    
    @autobind
    async loadExistingCompletions(switchState: boolean) {
        await this.getCompletions(true);
        
        if(this.cHasCompletions()) {
            let hasModReplies = false;
            if(this.tasksSite) {
                hasModReplies = await WCC.API.apiRequest({
                    url: `threads/${this.threadId}/modReplies/count`,
                    method: 'GET',
                }) != 0;
            }
            
            if(switchState && !hasModReplies) {
                this.oIsInSelectionState(true);
            }
        }
    }
    
    @autobind
    async getNewCompletions() {
        await this.getCompletions(false);
    }

    @autobind
    private async getCompletions(loadExisting: boolean) {
        if(this.busy())
            return;

        if(this.oSelectedView().type == enums.OpenAiReplySuggestionType.ThankYou) {
            if(this.oThankYouCompletions().length > 0) return;
        }
        else {
            if(this.oTellMeMoreCompletions().length > 0) return;
        }

        this.busy(true);
        try {
            const suggestions: IReplySuggestionJson[] = await WCC.API.apiRequest({
                url: `openai/threads/${this.threadId}/replySuggestions`,
                method: loadExisting ? 'GET' : 'POST',
            });
            
            this.fillSuggestions(suggestions);
            if(this.onSuggestionsLoaded)
                this.onSuggestionsLoaded(suggestions);
        }
        catch (e) {
            system.showFailedRequestMessage(e);
            console.warn(e);
        }
        this.busy(false);
    }
    
    fillSuggestions(suggestions: IReplySuggestionJson[]) {
        //console.log(suggestions)
        suggestions.forEach(s => {
            if(s.SuggestionType == enums.OpenAiReplySuggestionType.ThankYou.value)
                this.oThankYouCompletions.push(new OpenAiCompletionSentence(s, this.oTranslationNeeded));
            else
                this.oTellMeMoreCompletions.push(new OpenAiCompletionSentence(s, this.oTranslationNeeded));
        });
    }
    
    @autobind
    async selectCompletionView(view: OpenAiCompletionView, doNotLoadData: boolean = false) {
        if(view.oIsSelected()) return;
        this.oSelectedView().oIsSelected(false);
        view.oIsSelected(true);
        this.oSelectedView(view);
        
        if(view.type.value == GenerateCommentViewType.value) {
            this.oCommentGenerationPrompt('');
            this.oGenerateCommentCompletions([]);
            this.oCommentGenerationPromptFocused(true);
        }
        else {
            if(doNotLoadData === true) return;
            await this.getNewCompletions();
        }
    }
    
    @autobind
    async selectCompletionViewWithSwitch(view: OpenAiCompletionView) {
        await this.selectCompletionView(view);
        this.switchToSelectionState();
    }

    @autobind
    async getCommentGenerationCompletions() {
        if(system.isNullOrWhitespace(this.oCommentGenerationPrompt())) {
            system.showErrorMessage('Prompt is mandatory');
            return;
        }
        
        this.busy(true);
        try {
            const suggestions: IReplySuggestionJson[] = await WCC.API.apiRequest({
                url: `openai/threads/${this.threadId}/generateComments`,
                method: 'POST',
                data: {
                    Prompt: this.oCommentGenerationPrompt()
                }
            });

            this.oGenerateCommentCompletions(suggestions.map(s => new OpenAiCompletionSentence(s, this.oTranslationNeeded)));
        }
        catch (e) {
            system.showFailedRequestMessage(e);
            console.warn(e);
        }
        this.busy(false);
    }
    
    @autobind
    onCommentGenerationPromptKeyDown(obj:any, e: JQuery.Event) { // JQueryEventObject
        switch (e.keyCode) {
            case 13:
                this.getCommentGenerationCompletions();
                return false;
            default:
                return true;
        }
    }
    
    @autobind
    selectCompletion(sentence: OpenAiCompletionSentence) {
        if(this.onCompletionSelected) {
            this.onCompletionSelected(sentence.suggestionText, sentence.suggestionTranslatedText);
        }
        this.exitSelectionState();
    }
}